import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Paper, 
  IconButton, 
  Grid,
  Container,
  Chip
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Business, Engineering, Groups } from '@mui/icons-material';

// Imágenes del carrusel - puedes reemplazar por imágenes reales
const CAROUSEL_IMAGES = [
  { src: '/images/empresa/oficina1.jpg', alt: 'Oficinas CONEM', title: 'Nuestras Instalaciones' },
  { src: '/images/empresa/equipo.jpg', alt: 'Equipo de trabajo', title: 'Nuestro Equipo' },
  { src: '/images/empresa/proyecto1.jpg', alt: 'Proyecto en desarrollo', title: 'Proyectos Exitosos' },
  { src: '/images/empresa/tecnologia.jpg', alt: 'Tecnología avanzada', title: 'Tecnología de Vanguardia' },
  { src: '/images/empresa/clientes.jpg', alt: 'Reunión con clientes', title: 'Atención Personalizada' }
];

const NuestraEmpresa = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h3" color="primary" fontWeight="bold" gutterBottom>
          Nuestra Empresa
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Líderes en soluciones integrales de ingeniería y consultoría
        </Typography>
      </Box>

      {/* Carrusel de imágenes */}
      <Paper elevation={6} sx={{ borderRadius: 4, overflow: 'hidden', mb: 4 }}>
        <Box position="relative" height={400}>
          <Box
            component="img"
            src={CAROUSEL_IMAGES[currentImage].src}
            alt={CAROUSEL_IMAGES[currentImage].alt}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/800x400/1976d2/ffffff?text=' + encodeURIComponent(CAROUSEL_IMAGES[currentImage].title);
            }}
          />
          
          {/* Overlay con título */}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            bgcolor="rgba(0,0,0,0.7)"
            color="white"
            p={2}
          >
            <Typography variant="h5" fontWeight="bold">
              {CAROUSEL_IMAGES[currentImage].title}
            </Typography>
          </Box>

          {/* Botones de navegación */}
          <IconButton
            onClick={prevImage}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
            }}
          >
            <ArrowBackIos />
          </IconButton>
          
          <IconButton
            onClick={nextImage}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
            }}
          >
            <ArrowForwardIos />
          </IconButton>

          {/* Indicadores */}
          <Box
            position="absolute"
            bottom={60}
            left="50%"
            transform="translateX(-50%)"
            display="flex"
            gap={1}
          >
            {CAROUSEL_IMAGES.map((_, index) => (
              <Box
                key={index}
                width={12}
                height={12}
                borderRadius="50%"
                bgcolor={index === currentImage ? 'white' : 'rgba(255,255,255,0.5)'}
                sx={{ cursor: 'pointer' }}
                onClick={() => setCurrentImage(index)}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Contenido informativo */}
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
              Acerca de CONEM
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
              En CONEM nos dedicamos a brindar soluciones integrales de ingeniería y consultoría para el sector industrial y de servicios. Nuestro equipo está conformado por profesionales altamente capacitados, comprometidos con la excelencia y la innovación.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
              Contamos con más de 10 años de experiencia en el mercado, ofreciendo a nuestros clientes asesoría personalizada, proyectos llave en mano y soporte técnico de alta calidad. Nuestra misión es impulsar el desarrollo sostenible y la competitividad de nuestros aliados estratégicos.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
              Nos especializamos en proyectos de automatización industrial, sistemas de control, ingeniería de procesos y consultoría técnica especializada. Cada proyecto es único y desarrollamos soluciones a medida que se adapten perfectamente a las necesidades específicas de nuestros clientes.
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Card elevation={3} sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Experiencia
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Más de 10 años en el mercado brindando soluciones de calidad
                </Typography>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Card elevation={3} sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <Engineering sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Innovación
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tecnología de vanguardia y metodologías innovadoras
                </Typography>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Card elevation={3} sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <Groups sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Equipo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Profesionales altamente capacitados y comprometidos
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Valores y servicios */}
      <Box mt={6}>
        <Typography variant="h4" color="primary" fontWeight="bold" textAlign="center" gutterBottom>
          Nuestros Valores
        </Typography>
        <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} mt={3}>
          {['Excelencia', 'Innovación', 'Compromiso', 'Calidad', 'Confianza', 'Sostenibilidad'].map((valor) => (
            <Chip
              key={valor}
              label={valor}
              color="primary"
              variant="outlined"
              sx={{ fontSize: '1rem', py: 2, px: 1, fontWeight: 'bold' }}
            />
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default NuestraEmpresa;
